import { z } from "zod"

// Username rules and suggestion candidates. Pure and dependency-free on purpose:
// this module is imported by the server validator, by the availability endpoint,
// and by the signup forms, so it must not reach for React or the database.

export const USERNAME_MIN_LENGTH = 3
export const USERNAME_MAX_LENGTH = 16

// Alphanumeric runs joined by single hyphens. The alternating structure — rather
// than a character class — is what rejects leading, trailing and doubled hyphens
// and punctuation-only names, all in one pattern.
export const USERNAME_SHAPE_REGEX = /^[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/

// Self-contained variant that also bounds length, for callers without their own
// length check. The bound is a lookahead because a quantifier cannot bound an
// alternating structure.
export const USERNAME_REGEX = /^(?=[a-zA-Z0-9-]{3,16}$)[a-zA-Z0-9]+(?:-[a-zA-Z0-9]+)*$/

export const USERNAME_HINT = `${USERNAME_MIN_LENGTH} to ${USERNAME_MAX_LENGTH} characters. You may use letters, numbers and hyphens in between.`
export const USERNAME_LENGTH_ERROR = `Must be between ${USERNAME_MIN_LENGTH} and ${USERNAME_MAX_LENGTH} characters`
export const USERNAME_FORMAT_ERROR = "Only letters and numbers are allowed, with hyphens in between"

export function usernameFormatError(username: string): string | undefined {
  const value = username.trim()
  if (value.length < USERNAME_MIN_LENGTH || value.length > USERNAME_MAX_LENGTH) return USERNAME_LENGTH_ERROR
  if (!USERNAME_SHAPE_REGEX.test(value)) return USERNAME_FORMAT_ERROR
}

export type UsernameInput = { desired: string; email?: string | null }

const stripHyphens = (value: string) => value.replace(/-/g, "")

/**
 * Tokens from the email's local part only, so provider names never leak in.
 */
export function emailTokens(email?: string | null): string[] {
  if (!email) return []
  const at = email.lastIndexOf("@")
  if (at < 1) return []

  const local = email.slice(0, at).split("+")[0]
  return local
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 2)
    .slice(0, 4)
}

/**
 * Email tokens are only usable when at least one of them demonstrably relates to
 * the name the user typed. Without this gate someone entering "stargazer99" with
 * the address "xk7t9q2@proton.me" would be offered "xk7t9q2" — nonsense, and it
 * would surface a fragment of their address as a public handle.
 */
function tokensCorrelate(dNorm: string, tokens: string[]): boolean {
  if (!tokens.length) return false
  if (tokens.join("") === dNorm) return true

  return tokens.some(
    (token) =>
      (dNorm.startsWith(token) && dNorm.length - token.length >= 2) ||
      (dNorm.endsWith(token) && dNorm.length - token.length >= 2) ||
      (token.length >= 3 && dNorm.includes(token))
  )
}

// FNV-1a. Deterministic so suggestions are stable across calls and the tests need
// no seeding or fake randomness.
function hash(value: string): number {
  let h = 2166136261
  for (let i = 0; i < value.length; i++) {
    h ^= value.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

// Truncate the base, never the suffix, then drop any hyphen the cut exposed.
function withSuffix(base: string, suffix: string): string {
  const room = USERNAME_MAX_LENGTH - suffix.length
  const trimmed = (base.length > room ? base.slice(0, room) : base).replace(/-+$/, "")
  return trimmed + suffix
}

function* numberWalk(seed: string, min: number, span: number): Generator<number> {
  let h = hash(seed)
  const seen = new Set<number>()
  for (let guard = 0; guard < 500; guard++) {
    h = (Math.imul(h, 1103515245) + 12345) >>> 0
    const n = min + (h % span)
    if (seen.has(n)) continue
    seen.add(n)
    yield n
  }
}

/** Level 1 — the names a person would actually want. */
function level1(d: string, dNorm: string, tokens: string[]): string[] {
  const useEmail = tokensCorrelate(dNorm, tokens)

  const e1: string[] = []
  if (useEmail) {
    for (const token of tokens) {
      if (dNorm.startsWith(token) && dNorm.length - token.length >= 2) {
        e1.push(`${token}-${dNorm.slice(token.length)}`)
      }
      if (dNorm.endsWith(token) && dNorm.length - token.length >= 2) {
        e1.push(`${dNorm.slice(0, dNorm.length - token.length)}-${token}`)
      }
    }
  }

  const e2: string[] = []
  const e3: string[] = []
  if (useEmail) {
    for (let i = 0; i < tokens.length; i++) {
      for (let j = i + 1; j < tokens.length; j++) {
        e2.push(`${tokens[i]}-${tokens[j]}`)
        e3.push(tokens[i] + tokens[j])
      }
    }
  }

  // C1 — split at a letter/digit boundary. Needs no email.
  const c1: string[] = []
  const trailingDigits = d.match(/^([a-z0-9-]*[a-z])(\d+)$/)
  if (trailingDigits && trailingDigits[1].length >= 2 && trailingDigits[2].length >= 2) {
    c1.push(`${trailingDigits[1]}-${trailingDigits[2]}`)
  }
  const leadingDigits = d.match(/^(\d+)([a-z][a-z0-9-]*)$/)
  if (leadingDigits && leadingDigits[1].length >= 2 && leadingDigits[2].length >= 2) {
    c1.push(`${leadingDigits[1]}-${leadingDigits[2]}`)
  }

  // C2 — increment a trailing number. Length-preserving, so it is the one family
  // that stays clean on a name already at the maximum length.
  const c2: string[] = []
  const numbered = d.match(/^(.*?)(\d+)$/)
  if (numbered) {
    const start = parseInt(numbered[2], 10)
    for (let k = 1; k <= 4; k++) c2.push(withSuffix(numbered[1], String(start + k)))
  }

  // C3 — the hyphen-stripped form, which also frees up characters.
  const c3 = d.includes("-") ? [dNorm] : []

  // E4 — numbers on the best email-derived base.
  const e4: string[] = []
  const emailBase = e1[0] ?? e2[0]
  if (emailBase) for (let k = 1; k <= 3; k++) e4.push(withSuffix(emailBase, String(k)))

  // C4 — plain low numbers. Last, because they read like leftovers.
  const c4: string[] = []
  for (let k = 1; k <= 9; k++) c4.push(withSuffix(d, String(k)))

  return [...e1, ...e2, ...c1, ...c2, ...e3, ...c3, ...e4, ...c4]
}

/** Level 2 — widen the numeric space when the obvious names are gone. */
function level2(d: string): string[] {
  const out: string[] = []
  for (const n of numberWalk(d, 10, 990)) {
    out.push(withSuffix(d, String(n)))
    if (out.length >= 30) break
  }
  return out
}

/** Level 3 — high entropy. 9000 values per base makes exhaustion impractical. */
function level3(d: string): string[] {
  const out: string[] = []
  for (const n of numberWalk(`${d}!`, 1000, 9000)) {
    out.push(withSuffix(d, `-${n}`))
    if (out.length >= 30) break
  }
  return out
}

export function generateUsernameCandidates(
  input: UsernameInput,
  opts: { level?: 1 | 2 | 3; max?: number } = {}
): string[] {
  const { level = 1, max = 30 } = opts

  const d = input.desired.trim().toLowerCase()
  const dNorm = stripHyphens(d)
  if (!d) return []

  const raw =
    level === 1 ? level1(d, dNorm, emailTokens(input.email))
    : level === 2 ? level2(d)
    : level3(d)

  // Anything the form would reject is dropped here, which also absorbs every
  // truncation edge case without special-casing.
  const out: string[] = []
  const seen = new Set<string>()
  for (const candidate of raw) {
    const value = candidate.toLowerCase()
    // Only the typed name itself is excluded. When it contains hyphens, its
    // stripped form is a genuinely different name and a good suggestion.
    if (value === d) continue
    if (seen.has(value)) continue
    if (usernameFormatError(value)) continue
    seen.add(value)
    out.push(value)
    if (out.length >= max) break
  }
  return out
}

/**
 * Zod field for the signup forms. Client-side so a malformed name is answered on
 * blur, before any request and before submitting a whole form.
 */
export const usernameField = () =>
  z
    .string({ required_error: "Username is required" })
    .min(1, { message: "Username is required" })
    .superRefine((value, ctx) => {
      const error = usernameFormatError(value)
      if (error) ctx.addIssue({ code: z.ZodIssueCode.custom, message: error })
    })
